import { formatDate, formatTime } from '@/app/helpers/date';
import { NewPeriod } from '@/app/models/period';
import { useGenerateSignedLinkQuery } from '@/app/services/sharignResultApi';
import DownloadIcon from '@/icons/download.svg';
import {
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ActionMenu from '../common/ActionMenu';

const HeaderTableCell = styled(TableCell)(() => ({
  fontSize: 13,
  fontWeight: 'bold',
}));

const RowTableCell = styled(TableCell)(() => ({
  fontSize: 13,
}));

type PeriodTableProps = {
  rows: NewPeriod[];
  count: number;
  rowsPerPage: number;
  setRowsPerPage: (n: number) => void;
  page: number;
  setPage: (n: number) => void;
};

function FilePathLink(params: { period: string; processed: boolean }) {
  const { period, processed } = params;
  const { t } = useTranslation(['sharing']);
  const { data: file } = useGenerateSignedLinkQuery(period);
  return (
    <div style={{ minWidth: '135px', textAlign: 'start' }}>
      {file?.filePath ? (
        <div
          style={{
            width: '135px',
            textAlign: 'start',
          }}
        >
          <a
            href={file?.filePath}
            style={{
              textTransform: 'none',
              textDecoration: 'none',
              display: 'flex',
              margin: '8px',
              marginLeft: '4px',
            }}
          >
            <img src={DownloadIcon} alt="Icone de Download" />
            <Typography sx={{ fontSize: '13px', color: '#666666', marginLeft: '8px' }}>
              Download
            </Typography>
          </a>
        </div>
      ) : (
        <div
          style={{
            width: '135px',
            textAlign: 'start',
            textTransform: 'none',
            textDecoration: 'none',
            display: 'flex',
            margin: '8px',
            marginLeft: '4px',
            alignItems: 'center',
          }}
        >
          <img src={DownloadIcon} alt="Icone de Download" />
          {processed ? (
            <div
              style={{
                height: '8px',
                width: '100px',
                backgroundColor: '#f0f0f0',
                marginLeft: '10px',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  background:
                    'linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.6) 50%, rgba(255, 255, 255, 0) 100%)',
                  animation: 'shimmer 1.5s infinite',
                }}
              />
            </div>
          ) : (
            <Typography sx={{ fontSize: '13px', color: '#666666', marginLeft: '8px' }}>
              {t('not.processed')}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
}

export default function PeriodTable(props: PeriodTableProps) {
  const { i18n, t } = useTranslation(['sharing', 'common']);
  const { rows, count, rowsPerPage, setRowsPerPage, page, setPage } = props;

  return (
    <div>
      <TableContainer sx={{ marginLeft: 0, padding: 0, width: '100%' }}>
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <HeaderTableCell>{t('period')}</HeaderTableCell>
              <HeaderTableCell>{t('processing.date')}</HeaderTableCell>
              <HeaderTableCell align="right">{t('common:actions')}</HeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.date}>
                <RowTableCell>{row.date}</RowTableCell>
                <RowTableCell>
                  {`${formatDate(row.indirectSharingProcessingDate, i18n.language)} - ${formatTime(
                    row.indirectSharingProcessingDate,
                  )}  `}
                </RowTableCell>
                <RowTableCell align="right">
                  <ActionMenu>
                    <FilePathLink
                      period={row.date}
                      processed={!!row.indirectSharingProcessingDate}
                    />
                  </ActionMenu>
                </RowTableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell sx={{ textAlign: 'center' }} colSpan={4}>
                  <Typography variant="subtitle2">
                    {t('common:no.data.for.the.chosen.period.and.search.term')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          sx={{ width: '100%' }}
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={count || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelDisplayedRows={({ from, to, count: tcount }) => `${from}-${to} ${t('de')} ${tcount}`}
          labelRowsPerPage={t('common:items.per.page')}
        />
      </TableContainer>
    </div>
  );
}
